import { BookingFormService } from '../../api/services/BookingFormService';
import { BookingFormSettingService } from '../../api/services/BookingFormSettingService';
import { BookingFormQuestionService } from '../../api/services/BookingFormQuestionService';
import { BookingFormUserService } from '../../api/services/BookingFormUserService';
import type { Routemize_DTOs_BookingFormDto } from '../../api/models/Routemize_DTOs_BookingFormDto';
import type { Routemize_DTOs_CheckSlugAvailabilityRequest } from '../../api/models/Routemize_DTOs_CheckSlugAvailabilityRequest';
import type { Routemize_DTOs_SlugAvailabilityDto } from '../../api/models/Routemize_DTOs_SlugAvailabilityDto';
import type { Routemize_DTOs_UpdateBookingFormDto } from '../../api/models/Routemize_DTOs_UpdateBookingFormDto';
import type { Routemize_DTOs_BookingFormSettingDto } from '../../api/models/Routemize_DTOs_BookingFormSettingDto';
import type { Routemize_DTOs_UpdateBookingFormSettingDto } from '../../api/models/Routemize_DTOs_UpdateBookingFormSettingDto';
import type { Routemize_DTOs_BookingFormRequestDTO } from '../../api/models/Routemize_DTOs_BookingFormRequestDTO';
import type { Routemize_DTOs_CreateBookingFormSettingDto } from '../../api/models/Routemize_DTOs_CreateBookingFormSettingDto';
import type { Routemize_DTOs_CreateFormQuestionDto } from '../../api/models/Routemize_DTOs_CreateFormQuestionDto';
import type { Routemize_DTOs_UpdateFormQuestionDto } from '../../api/models/Routemize_DTOs_UpdateFormQuestionDto';
import type { Routemize_DTOs_CreateBookingFormDto } from '../../api/models/Routemize_DTOs_CreateBookingFormDto';
import type { Routemize_DTOs_AssignBookingUser } from '../../api/models/Routemize_DTOs_AssignBookingUser';
import type { Routemize_DTOs_UpdateBookingFormRequestDTO } from '../../api/models/Routemize_DTOs_UpdateBookingFormRequestDTO';
/** Booking form with settings loaded (for editing) */
export type BookingFormWithSettings = Routemize_DTOs_BookingFormDto & {
  bookingFormSettings?: Routemize_DTOs_BookingFormSettingDto;
  /** Set when duplicating — the original form's ID, used to load questions & fields */
  sourceFormId?: string;
  /**
   * True while `bookingFormSettings` is still in flight. The editor opens on the
   * list row's data so it paints immediately, so an absent `bookingFormSettings`
   * means "not loaded yet" rather than "this form has none" until this clears.
   */
  settingsLoading?: boolean;
};

/** Payload from the create/edit modal — field names match generated DTOs */
export interface BookingFormSavePayload {
  formDetail: Routemize_DTOs_CreateBookingFormDto;
  settings?: Partial<Routemize_DTOs_BookingFormSettingDto>;
  assignedUserIds: string[];
  questionsToCreate: Routemize_DTOs_CreateFormQuestionDto[];
  questionsToUpdate: Routemize_DTOs_UpdateFormQuestionDto[];
  questionIdsToDelete: string[];
  defaultFields?: Array<{ id: string; isEnabled: boolean; isRequired: boolean }>;
}

/**
 * BookingFormManager - Thin service layer for booking form operations.
 * All types are generated DTOs — no custom mapping.
 */
export class BookingFormManager {

  private static readonly MAX_DUPLICATE_SLUG_ATTEMPTS = 10;

  // ============================================
  // Core CRUD Operations
  // ============================================

  async getAll(maxResults: number = 100): Promise<Routemize_DTOs_BookingFormDto[]> {
    const response = await BookingFormService.getApiAppBookingFormGetList(
      undefined,
      0,
      maxResults
    );
    return response.items || [];
  }

  async getById(id: string): Promise<Routemize_DTOs_BookingFormDto> {
    return await BookingFormService.getApiAppBookingFormGetById(id);
  }

  async getWithSettings(id: string): Promise<BookingFormWithSettings> {
    const [form, settings] = await Promise.all([
      this.getById(id),
      this.fetchBookingFormSettings(id),
    ]);
    return { ...form, bookingFormSettings: settings || undefined };
  }

  async createForm(dto: Routemize_DTOs_BookingFormRequestDTO): Promise<Routemize_DTOs_BookingFormDto> {
    return await BookingFormService.postApiAppBookingFormCreateForm(dto);
  }

  async update(dto: Routemize_DTOs_UpdateBookingFormDto): Promise<Routemize_DTOs_BookingFormDto> {
    return await BookingFormService.putApiAppBookingFormUpdate(dto);
  }

  // ============================================
  // Lifecycle Operations
  // ============================================

  async publish(id: string): Promise<void> {
    await BookingFormService.postApiAppBookingFormPublishBookingForm(id);
  }

  async unpublish(id: string): Promise<void> {
    await BookingFormService.postApiAppBookingFormUnpublishBookingForm(id);
  }

  async archive(id: string): Promise<void> {
    await BookingFormService.postApiAppBookingFormArchiveBookingForm(id);
  }

  async delete(id: string): Promise<void> {
    await BookingFormService.deleteApiAppBookingFormDelete(id);
  }

  // ============================================
  // Slug Availability (for the optional short-URL code)
  // ============================================

  /**
   * Checks whether the supplied slug is valid AND not taken by another form in the
   * current tenant. Returns the server's normalized form, a reason on failure, and
   * a suggested alternative when the slug is taken.
   */
  async checkSlugAvailability(
    slug: string,
    excludeFormId?: string
  ): Promise<Routemize_DTOs_SlugAvailabilityDto> {
    const request: Routemize_DTOs_CheckSlugAvailabilityRequest = {
      slug,
      excludeFormId,
    };
    return await BookingFormService.postApiAppBookingFormCheckSlugAvailability(request);
  }

  /**
   * Resolves a free short-URL code for a duplicated form: `foo` → `foo-copy`,
   * then `foo-copy-2`, `foo-copy-3`… An existing copy suffix on the source is
   * stripped first so duplicating a copy never yields `foo-copy-copy`.
   * Gives up after {@link BookingFormManager.MAX_DUPLICATE_SLUG_ATTEMPTS} probes
   * and returns the last candidate — the editor's own live check surfaces the
   * collision if that one is taken too.
   */
  async resolveDuplicateSlug(sourceSlug: string): Promise<string> {
    const base = sourceSlug.trim().replace(/-copy(-\d+)?$/i, '').replace(/-+$/, '');
    if (!base) return sourceSlug.trim();

    const tried = new Set<string>();
    let candidate = `${base}-copy`;

    for (let suffix = 2; suffix <= BookingFormManager.MAX_DUPLICATE_SLUG_ATTEMPTS; suffix++) {
      tried.add(candidate);
      const result = await this.checkSlugAvailability(candidate);
      if (result.available) return result.normalized?.trim() || candidate;

      const suggestion = result.suggestedAlternative?.trim();
      candidate = suggestion && !tried.has(suggestion) ? suggestion : `${base}-copy-${suffix}`;
    }

    return candidate;
  }

  // ============================================
  // Settings Management
  // ============================================

  async fetchBookingFormSettings(formId: string): Promise<Routemize_DTOs_BookingFormSettingDto | null> {
    try {
      const response = await BookingFormSettingService.getApiAppBookingFormSettingGetByFormId(formId);
      return response || null;
    } catch (error: any) {
      const errorDetails = error?.body?.error?.details || error?.error?.details || '';
      if (error?.status === 404 || errorDetails.includes('EntityNotFoundException')) {
        return null;
      }
      throw error;
    }
  }

  async createOrUpdateBookingFormSettings(
    settings: Routemize_DTOs_UpdateBookingFormSettingDto,
    formId: string
  ): Promise<void> {
    const existingSettings = await this.fetchBookingFormSettings(formId);
    const settingsDto: Routemize_DTOs_UpdateBookingFormSettingDto = {
      ...settings,
      bookingFormId: formId,
    };

    if (existingSettings) {
      await BookingFormSettingService.putApiAppBookingFormSettingUpdate(settingsDto);
    } else {
      await BookingFormSettingService.postApiAppBookingFormSettingCreate(
        settingsDto as Routemize_DTOs_CreateBookingFormSettingDto
      );
    }
  }

  // ============================================
  // User Assignment
  // ============================================

  async syncAssignedUsers(
    formId: string,
    userIds: string[]
  ): Promise<void> {
    // Send the full target user list — the backend handles add/remove diff
    const payload: Routemize_DTOs_AssignBookingUser = {
      bookingFormId: formId,
      userIds: userIds,
    };
    await BookingFormUserService.postApiAppBookingFormUserCreateMany(payload);
  }

  async updateForm(
    formDetail: Routemize_DTOs_UpdateBookingFormDto,
    assignedUserIds: string[]
  ): Promise<Routemize_DTOs_BookingFormDto> {
    const payload: Routemize_DTOs_UpdateBookingFormRequestDTO = {
      formDetail,
      assignedUserIds,
    };
    return await BookingFormService.putApiAppBookingFormUpdateForm(payload);
  }

  // ============================================
  // Question CRUD Operations
  // ============================================

  async handleQuestionUpdates(
    formId: string,
    questionsToCreate: Routemize_DTOs_CreateFormQuestionDto[],
    questionsToUpdate: Routemize_DTOs_UpdateFormQuestionDto[],
    questionIdsToDelete: string[]
  ): Promise<void> {
    const errors: Error[] = [];

    if (questionIdsToDelete.length > 0) {
      for (const questionId of questionIdsToDelete) {
        try {
          await BookingFormQuestionService.deleteApiAppBookingFormQuestionDelete(questionId);
        } catch (error) {
          console.error('Failed to delete question:', questionId, error);
          errors.push(error instanceof Error ? error : new Error(`Failed to delete question ${questionId}`));
        }
      }
    }

    if (questionsToUpdate.length > 0) {
      for (const question of questionsToUpdate) {
        try {
          const updateDto: Routemize_DTOs_UpdateFormQuestionDto = {
            ...question,
            bookingFormId: formId,
          };
          await BookingFormQuestionService.putApiAppBookingFormQuestionUpdate(updateDto);
        } catch (error) {
          console.error('Failed to update question:', question.id, error);
          errors.push(error instanceof Error ? error : new Error(`Failed to update question ${question.id}`));
        }
      }
    }

    if (questionsToCreate.length > 0) {
      const createDtos: Routemize_DTOs_CreateFormQuestionDto[] = questionsToCreate.map(q => ({
        ...q,
        bookingFormId: formId,
      }));

      try {
        await BookingFormQuestionService.postApiAppBookingFormQuestionCreateMany(createDtos);
      } catch (error) {
        console.error('Failed to create questions:', error);
        errors.push(error instanceof Error ? error : new Error('Failed to create questions'));
      }
    }

    if (errors.length > 0) {
      throw new Error(`${errors.length} question operation(s) failed. Check console for details.`);
    }
  }

  // ============================================
  // Favorites
  // ============================================

  async getFavoriteFormIds(): Promise<string[]> {
    const result = await BookingFormService.getApiAppBookingFormGetFavoriteFormIds();
    return (result as string[]) || [];
  }

  async toggleFavorite(bookingFormId: string): Promise<void> {
    await BookingFormService.postApiAppBookingFormToggleFavorite(bookingFormId);
  }
}

export const bookingFormManager = new BookingFormManager();
